import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useRef } from "react";
import * as yup from "yup";
import useColors from "../../../../api/hooks/useColors";
import FormTextField from "../../../../components/forms/FormTextField";
import LoadingIcon from "../../../../components/LoadingIcon";
import RedAsterisk from "../../../../components/RedAsterisk";
import { getIncludedEntityIds } from "../../../posture/controls/components/EntitiesSection/utils";
import ServiceNowCategory from "./fields/ServiceNowCategory";
import ServiceNowIncludeEntities from "./fields/ServiceNowIncludeEntities";
import ServiceNowUrgency from "./fields/ServiceNowUrgency";

const SERVICENOW_FORM_WIDTH = "450px";
const SERVICENOW_FORM_HEIGHT = "600px";

const ServiceNowIncidentCreationModal = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  fullControl,
}) => {
  const formRef = useRef(null);
  const colors = useColors();

  const defaultShortDescription = fullControl?.name;
  const defaultDescription =
    fullControl?.name_for_gap_state +
    "\n\n*Remediation Steps*\n" +
    fullControl?.remediation.remediation_steps_summary
      .map((step, index) => `${index + 1}. ${step}`)
      .join("\n");

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  const formInitialValues = {
    shortDescription: defaultShortDescription || "",
    description: defaultDescription || "",
    urgency: "",
    category: "",
    includeEntities: getIncludedEntityIds(fullControl)?.length > 0,
  };

  const formValidationSchema = yup.object().shape({
    shortDescription: yup.string().required("Short description is required"),
    description: yup.string().required("Description is required"),
    urgency: yup.string().required("Urgency is required"),
    category: yup.string().required("Category is required"),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: colors.jira_modal_bg,
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 0 }}>
        <Typography variant="subtitle1" color={colors.text_primary}>
          Create ServiceNow Incident
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "&:first-of-type": {
            paddingTop: 0,
          },
        }}
      >
        {/* This Box keeps the sub-titles on top when scrolling down the modal */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: colors.jira_modal_bg,
            zIndex: 1,
            padding: "16px 0",
          }}
        >
          <Box sx={{ marginBottom: "16px" }}>
            <Typography variant="body2" color={colors.text_secondary}>
              Creating an incident for Control #{fullControl?.control_id}
            </Typography>
          </Box>
          <Box
            sx={{
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Typography variant="caption" color={colors.text_secondary}>
              Required fields are marked with an asterisk{" "}
            </Typography>
            <RedAsterisk />
          </Box>
        </Box>

        <Box
          sx={{ width: SERVICENOW_FORM_WIDTH, height: SERVICENOW_FORM_HEIGHT }}
        >
          <Formik
            innerRef={formRef}
            onSubmit={handleSubmit}
            initialValues={formInitialValues}
            validationSchema={formValidationSchema}
            enableReinitialize
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Short description field */}
                  <FormTextField
                    label="Short Description"
                    fieldName="shortDescription"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor:
                          colors.textfield_outlined_enabled_auto_generated_bg,
                      },
                    }}
                  />

                  {/* Description field */}
                  <FormTextField
                    label="Description"
                    fieldName="description"
                    required
                    multiline
                    rows={6}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor:
                          colors.textfield_outlined_enabled_auto_generated_bg,
                      },
                    }}
                  />

                  <ServiceNowUrgency />
                  <ServiceNowCategory />
                  <ServiceNowIncludeEntities />
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </DialogContent>

      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
          margin: "0 24px 12px",
        }}
      >
        <DialogActions>
          {/* Styling this button in-place until we have a button-text-inherit style button */}
          <Button
            variant="text"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              backgroundColor: colors.button_text_inherit_enabled_bg,
              color: colors.button_text_inherit_enabled_text,
              borderRadius: "10px",
              minWidth: "64px",
              padding: "7px 14.5px",
              "&:hover": {
                backgroundColor: colors.button_text_inherit_hovered_bg,
              },
            }}
          >
            <Typography variant="overline">Cancel</Typography>
          </Button>
          {/* Styling this button in-place until we have a button-contained-primary style button */}
          <Button
            variant="contained"
            onClick={() => {
              formRef.current?.submitForm();
            }}
            disabled={isLoading}
            startIcon={isLoading ? <LoadingIcon size={16} /> : null}
            sx={{
              backgroundColor: colors.button_contained_primary_enabled_bg,
              color: colors.button_contained_primary_enabled_text,
              borderRadius: "10px",
              minWidth: "64px",
              padding: "7px 14.5px",
              "&:hover": {
                backgroundColor: colors.button_contained_primary_hovered_bg,
              },
            }}
          >
            <Typography variant="overline">
              {isLoading ? "Creating..." : "Create"}
            </Typography>
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ServiceNowIncidentCreationModal;
